import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';

import tag from '@bytel/bytel-tag-commander';
import { CheckoutStorageService, Oauth2Service, YAMA_STORAGE_KEY } from '@bytel/checkout';

import { BasicObject } from '@base/common/models/services.interface';

import { ConfigurationService } from '@services/configuration.service';

import { ENV_LIST, IInitialConfig } from '@interfaces/config.interface';

import { Observable, from, mergeMap, of } from 'rxjs';

import { initialConfig } from '../../../environments/environment';
import { ACTION_CLICK, Tags } from './tag.interface';

@Injectable({
    providedIn: 'root',
})
export class TagService {
    public currentCheckoutTags: Tags;

    protected initialized: boolean = false;
    protected settings: BasicObject = {};
    private _initialConfig: IInitialConfig;

    constructor(
        private oauth2: Oauth2Service,
        private configurationService: ConfigurationService,
        protected checkoutStorage: CheckoutStorageService,
        @Inject(DOCUMENT) private document: Document,
    ) {
        this._initialConfig = initialConfig();
    }

    /**
     * Filter string before sending tag
     */
    public filter(str: string): string {
        return str
            .split(' ')
            .join('_')
            .split('é')
            .join('e')
            .split('è')
            .join('e')
            .split('ê')
            .join('e')
            .split('à')
            .join('a');
    }

    public filterTags(tags: BasicObject): BasicObject {
        return tags;
    }

    /**
     * Push data to Tags service
     */
    public send(data: BasicObject): void {
        try {
            if (!this.initialized) {
                console.error('Please initialize service before use');
                return;
            }
            if (!data || !Object.keys(data).length) {
                console.error('Please provide some data for tag service');
                return;
            }
            data.env = data.env || this._initialConfig.environment;
            const yamaCart: string = this.checkoutStorage.get(YAMA_STORAGE_KEY, false);
            if (yamaCart) {
                data.site = 'Partenaire_cfcm_boutique';
            } else {
                data.site = data.site || 'Boutique';
            }
            const windowObj: Window = this.document.defaultView.window;
            data.univers = data.univers || windowObj.tc_vars.univers || this.currentCheckoutTags.univers;
            data.parcours = data.parcours || windowObj.tc_vars.parcours || this.currentCheckoutTags.parcours;

            data.url = data.url || windowObj.location.href;

            const tagService = tag;
            if (!tagService) {
                console.error('Tag package is not defined');
                return;
            }
            data = this.filterTags(data);
            data.variablesSpecifiques = { ...data.variablesSpecifiques, ...{ version: this._initialConfig.version } };
            from(tagService.send(data)).subscribe(() => {
                if (this.settings.debug) {
                    console.table(data);
                }
            });
        } catch (error) {
            console.log(error);
            return;
        }
    }

    public initialize(): Observable<boolean> {
        const tagService = tag;
        this.setup();
        return from(tagService.setup(this.settings)).pipe(
            mergeMap(() => {
                this.initialized = true;
                return of(true);
            }),
        );
    }

    public sendEvent(
        clickName: string,
        clickCategory: string,
        clickAction: string,
        additionnalTags: BasicObject = {},
    ): void {
        this.send({
            event: 'clic',
            nomClic: clickName,
            categorieClic: clickCategory,
            actionClic: ACTION_CLICK[clickAction] || clickAction,
            ...additionnalTags,
        });
    }

    public store(data: BasicObject): void {
        sessionStorage.removeItem('tc_var');
        sessionStorage.setItem('tc_var', JSON.stringify(data));
    }

    public resetInit(): void {
        this.initialized = false;
    }

    /**
     * Bare settings for Tag service
     */
    protected setup(options: BasicObject = {}): void {
        this.settings = {
            // Website API url
            host: this._initialConfig.oauth.resourceUrl, // Api env execution
            env: this._initialConfig.oauth.xBanc, // App name which use the lib
            site: 'Boutique', // debug, enable by default on LOCAL env, but can be disable with provided options
            debug: this._initialConfig.environment !== ENV_LIST.PRODUCTION,
            shouldEmitEvent: true,
            hasHistory: true,
        };

        if (this.oauth2.updateIsConnected()) {
            this.oauth2.initToken();
            this.settings.oauth = {
                idToken: this.oauth2._id_token,
                accessToken: this.oauth2.access_token,
            };
        } else {
            this.settings.oauth = {
                callback: this.configurationService.getData()?.picasso?.emptycallBackUrl,
                clientId: this.configurationService.getData()?.oauth?.clientId || 'magento.web.bouyguestelecom.fr',
                url: this.configurationService.getData()?.oauth?.baseUrl,
            };
        }

        this.settings = {
            ...this.settings,
            options,
        };
    }
}
